import { omit, sortBy, uniq, uniqBy } from 'lodash'
import {
  PRICE_BOOK_ITEM_TYPES,
  type PriceBookItemType,
  parsePriceBookItemType,
} from '@/api/types/priceBookTypes'
import { type LineItem, type LineItemDetails } from '@/models/line-item'
import type PriceBookItem from '@/models/price-book-item'
import type User from '@/models/user'

/** A custom line item's type comes from the same set as a price book item's. */
export type LineItemType = PriceBookItemType

/** "Service (Hourly labour • Regular)" → "Service / Hourly labour / Regular" */
export function formatLineItemType(type: LineItemType) {
  return type.replace(' (', ' / ').replace(/\)$/, '').split(' • ').join(' / ')
}

const category = (type: LineItemType) => type.slice(0, type.indexOf(' ('))

/** Grouped by category with "Other" last; otherwise in backend enum order. */
export const LINE_ITEM_TYPE_OPTIONS: { text: string; value: LineItemType }[] = sortBy(
  Object.values(PRICE_BOOK_ITEM_TYPES),
  [category, (type) => type.endsWith('(Other)')],
).map((type) => ({ text: formatLineItemType(type), value: type }))

/**
 * The type that decides a line item's type-specific fields: its price book
 * item's type when linked, otherwise the type picked for a custom item.
 */
export function resolveLineItemType(
  lineItem: LineItem,
  priceBookItems: Pick<PriceBookItem, 'id' | 'type'>[] | null | undefined,
): LineItemType | null {
  if (lineItem.priceBookItemId) {
    const priceBookItem = priceBookItems?.find((item) => item.id === lineItem.priceBookItemId)
    return parsePriceBookItemType(priceBookItem?.type)
  }
  return isCustomLineItem(lineItem) ? lineItem.type : null
}

export type WorkerOption =
  | {
      text: string
      /** The user's uuid. */
      value: string
    }
  | { header: string }

type WorkerUser = Pick<User, 'uuid' | 'name'>

/**
 * Worker picker options: the job's crew first under its own heading, then
 * everyone else. Shows each user's name, keeps their uuid. Order is preserved.
 */
export function toWorkerOptions(crew: WorkerUser[], others: WorkerUser[]): WorkerOption[] {
  const toOption = (user: WorkerUser) => ({ text: user.name, value: user.uuid })
  const crewOptions = uniqBy(crew, 'uuid').map(toOption)
  const crewIds = new Set(crewOptions.map((option) => option.value))
  const otherOptions = uniqBy(others, 'uuid')
    .filter((user) => !crewIds.has(user.uuid))
    .map(toOption)

  if (crewOptions.length === 0) return otherOptions
  return [
    { header: 'Assigned to job' },
    ...crewOptions,
    ...(otherOptions.length > 0 ? [{ header: 'Other' }, ...otherOptions] : []),
  ]
}

/** A free-form name rather than a price book item; only these get a type. */
export function isCustomLineItem(lineItem: LineItem) {
  return !lineItem.priceBookItemId && lineItem.name.trim() !== ''
}

/** "More details" fields that only some line item types show. */
export type LineItemTypeDetailField = Exclude<
  keyof LineItemDetails,
  'description' | 'referenceNumber'
>

const LABOUR_FIELDS: LineItemTypeDetailField[] = ['workerId', 'workDate']
const EQUIPMENT_FIELDS: LineItemTypeDetailField[] = ['serialNumber', 'workDate']

/** Which type-specific fields each line item type shows; unlisted types show none. */
const DETAIL_FIELDS_BY_TYPE: Partial<Record<LineItemType, LineItemTypeDetailField[]>> = {
  [PRICE_BOOK_ITEM_TYPES.ServiceHourlyRegular]: LABOUR_FIELDS,
  [PRICE_BOOK_ITEM_TYPES.ServiceHourlyOvertime]: LABOUR_FIELDS,
  [PRICE_BOOK_ITEM_TYPES.ServiceHourlyStandby]: LABOUR_FIELDS,
  [PRICE_BOOK_ITEM_TYPES.ServiceHourlyTravel]: LABOUR_FIELDS,
  [PRICE_BOOK_ITEM_TYPES.AccessorialEquipmentHourly]: EQUIPMENT_FIELDS,
  [PRICE_BOOK_ITEM_TYPES.AccessorialEquipmentFlat]: EQUIPMENT_FIELDS,
  [PRICE_BOOK_ITEM_TYPES.ProductParts]: ['storageLocation'],
  [PRICE_BOOK_ITEM_TYPES.ProductSerializedParts]: ['serialNumber', 'storageLocation'],
}

const TYPE_DETAIL_FIELDS = uniq(Object.values(DETAIL_FIELDS_BY_TYPE).flat())

/** Fields that identify one physical item, so a duplicated line item doesn't copy them. */
const UNIQUE_DETAIL_FIELDS: LineItemTypeDetailField[] = ['serialNumber']

export function getDetailFieldsForType(type: LineItemType | null): LineItemTypeDetailField[] {
  return (type && DETAIL_FIELDS_BY_TYPE[type]) || []
}

/**
 * Drops type-specific fields the given type doesn't show, so hidden values
 * can't resurface later. Fields every item shows are kept.
 */
export function pruneDetailsForType(
  details: LineItemDetails,
  type: LineItemType | null,
): LineItemDetails {
  const shown = getDetailFieldsForType(type)
  return omit(
    details,
    TYPE_DETAIL_FIELDS.filter((field) => !shown.includes(field)),
  )
}

/** The details a duplicated line item should start with. */
export function detailsForDuplicate(details: LineItemDetails): LineItemDetails {
  return omit(details, UNIQUE_DETAIL_FIELDS)
}
